import { useEffect, useState } from 'react';

import { matchesApiErrorStatus } from '@/shared/error/isZepQuizApiError';

const RECOVERABLE_CURSOR_ERRORS = [
  'INVALID_CURSOR',
  'EXPIRED_CURSOR',
  'CURSOR_QUERY_MISMATCH',
] as const;

export const isRecoverableCursorError = (error: unknown) =>
  matchesApiErrorStatus(error, [...RECOVERABLE_CURSOR_ERRORS]);

/** `idle` → 복구할 오류를 받으면 `recovering` → 다시 읽고도 오류가 남으면 `exhausted`. 오류가 풀리면 `idle`. */
type RecoveryPhase = 'idle' | 'recovering' | 'exhausted';

/**
 * 커서가 무효해지면(서버 배포로 형식이 바뀜·만료·조건 불일치) 커서를 버리고 첫 페이지부터 한 번 다시 읽는다.
 *
 * `shouldMaskError` 동안은 소비 쪽이 `isError` 를 내리지 않아야 한다 — 다시 읽는 동안에도 쿼리는
 * 오류 상태로 남아 있어서 그대로 내보내면 목록이 오류 화면으로 깜빡인다. 다시 읽고도 실패하면
 * 가리지 않으므로 그때는 오류 화면이 뜬다.
 */
export const useCursorQueryRecovery = ({
  isError,
  error,
  hasLoadedPages,
  refetch,
}: {
  isError: boolean;
  error: unknown;
  hasLoadedPages: boolean;
  /** 다시 읽은 결과의 `isError` 로 복구 성공 여부를 판단한다. React Query `refetch` 를 그대로 넘긴다. */
  refetch: () => Promise<{ isError: boolean }>;
}) => {
  const [phase, setPhase] = useState<RecoveryPhase>('idle');
  const shouldRecover =
    isError && hasLoadedPages && isRecoverableCursorError(error);

  useEffect(() => {
    if (!isError) {
      setPhase('idle');
      return;
    }
    if (!shouldRecover || phase !== 'idle') return;

    setPhase('recovering');
    // 다시 읽고도 오류면 `exhausted` 로 남아 오류를 드러낸다.
    void refetch().then(
      result => setPhase(result.isError ? 'exhausted' : 'idle'),
      () => setPhase('exhausted'),
    );
  }, [isError, phase, refetch, shouldRecover]);

  return {
    isRecovering: phase === 'recovering',
    shouldMaskError: shouldRecover && phase !== 'exhausted',
  };
};
